import type { ReactNode } from "react";
import { DismissControl } from "@/components/pages/DismissableAd";
import { cn } from "@/components/ui/cn";
import { frameMaxWidthPx } from "@/lib/placements/slots";

/**
 * The one visual unit every ad surface renders — the static slots, the
 * editor's three sections (booked, hand-authored, and the unsold mockup) and
 * the floating side unit: an editorial frame, not a promo box.
 *
 * A caption line above the creative carries the legal label on the left
 * ("Ad", t.pages.adBadge — text content, which ad blockers cannot filter) and
 * the session close on the right, so neither sits on the sponsor's artwork
 * and the close is always where the eye expects it. Below, the creative in a
 * box whose aspect ratio is reserved before paint (zero layout shift), on a
 * hairline, with the card radius the rest of the page uses. No brand ring, no
 * hover shadow: an ad on a well-set page is quiet, clearly labelled and easy
 * to put away. The box surface is the card colour, not the muted grey: a
 * creative of another proportion than the slot sits in it with a margin
 * (never cropped — the admin's promise), and that margin must read as
 * breathing room, not as grey bars.
 *
 * The frame IS the creative's box: it takes the layout's width up to the
 * shape's natural maximum (`frameMaxWidthPx`, from the aspect ratio — never
 * from the format's name, which the backend has already changed once), so a
 * 6:5 rectangle is a rectangle and not a 970 px-wide billboard with a
 * rectangle floating inside it, and the label and the close sit at the
 * creative's own corners. Children fill the box (`absolute inset-0`).
 */
export function PlacementFrame({
  label,
  ratio,
  format,
  className,
  children,
}: {
  /** The legal label — `t.pages.adBadge`. */
  label: string;
  /** CSS `aspect-ratio` for the box, from `aspectRatioCss`. */
  ratio: string;
  /** The IAB format — only consulted for the width cap when `ratio` is not
   *  `w / h` (the mockup passes the format's own ratio, so never in practice). */
  format?: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div
      className={cn("lh-placement mx-auto w-full", className)}
      style={{ maxWidth: frameMaxWidthPx(ratio, format) }}
    >
      <div className="mb-1 flex h-6 items-center justify-between gap-3">
        <span className="text-muted-foreground text-[0.625rem] font-medium uppercase leading-none tracking-[0.18em]">
          {label}
        </span>
        <DismissControl className="-mr-1.5" />
      </div>
      <div
        className="ring-border/70 relative overflow-hidden rounded-card bg-card ring-1"
        style={{ aspectRatio: ratio }}
      >
        {children}
      </div>
    </div>
  );
}
